import React, { useState, useEffect, useRef } from 'react';
import { PenLine, Play, Pause, RotateCcw, CheckCircle2, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { api } from '../lib/api';

const PROMPT =
  "Write about a decision you made in survival mode \u2014 a moment where you told yourself \"I\u2019ll never let this happen to me again\" or \"I have to be stronger.\" What were you protecting? What has that decision cost you since?";
const DURATION_SECONDS = 180;

const format = (s) => {
  const m = Math.floor(s / 60).toString().padStart(1, '0');
  const sec = (s % 60).toString().padStart(2, '0');
  return `${m}:${sec}`;
};

const InteractiveExercise = () => {
  const [running, setRunning] = useState(false);
  const [remaining, setRemaining] = useState(DURATION_SECONDS);
  const [text, setText] = useState('');
  const [saveEmail, setSaveEmail] = useState('');
  const [state, setState] = useState('idle'); // idle | saved
  const [saving, setSaving] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (running && remaining > 0) {
      timer.current = setTimeout(() => setRemaining((r) => r - 1), 1000);
    } else if (remaining === 0) {
      setRunning(false);
    }
    return () => clearTimeout(timer.current);
  }, [running, remaining]);

  const reset = () => {
    setRunning(false);
    setRemaining(DURATION_SECONDS);
  };

  const finish = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await api.exercise({
        response: text.trim(),
        email: saveEmail.trim() || undefined,
        prompt: PROMPT,
      });
      setState('saved');
    } catch (_e) {
      // Even if save fails, don't hide the user's writing
      setState('saved');
    } finally {
      setSaving(false);
    }
  };

  const progress = ((DURATION_SECONDS - remaining) / DURATION_SECONDS) * 100;

  return (
    <section id="try-gasoline" className="relative py-20 bg-[#0a0a0a]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <div className="inline-flex items-center space-x-2 bg-[#E63946]/10 border border-[#E63946]/30 rounded-full px-4 py-1 mb-4">
            <PenLine className="w-4 h-4 text-[#E63946]" />
            <span className="text-[#E63946] font-bold text-xs tracking-wider">TRY ONE EXERCISE NOW</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: '"Bebas Neue", sans-serif' }}>
            Feel the work before you buy the book
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            One prompt. Three minutes. No account. No judgement. Just you and a page.
          </p>
        </div>

        <div className="bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border border-[#D4AF37]/20 rounded-3xl p-6 md:p-10">
          {state === 'saved' ? (
            <div className="text-center py-6" data-testid="exercise-success">
              <CheckCircle2 className="w-16 h-16 text-[#D4AF37] mx-auto mb-4" />
              <h3 className="text-3xl font-bold text-white mb-3" style={{ fontFamily: '"Bebas Neue", sans-serif' }}>
                You just did the work
              </h3>
              <p className="text-gray-300 max-w-xl mx-auto mb-6 leading-relaxed">
                That was one of 40+ prompts inside Gasoline I. Every chapter ends with one.
                Every prompt is a shovel. Every shovel gets you closer to the root.
              </p>
              <a
                href="https://a.co/d/0fm0QG50"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="exercise-cta-amazon"
                className="inline-flex items-center justify-center gap-2 max-w-full w-full sm:w-auto bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0a0a0a] hover:from-[#FFD700] hover:to-[#D4AF37] font-bold px-6 sm:px-8 py-4 rounded-lg text-base sm:text-lg whitespace-normal leading-tight break-words transition-all duration-300 transform hover:scale-105"
              >
                Get Gasoline I on Amazon
                <ArrowRight className="w-5 h-5 flex-shrink-0" />
              </a>
            </div>
          ) : (
            <>
              <div className="bg-[#0a0a0a] border-l-4 border-[#D4AF37] rounded-r-lg px-6 py-4 mb-6">
                <p className="text-xs font-bold text-[#D4AF37] tracking-wider mb-2">THE PROMPT</p>
                <p className="text-white text-lg leading-relaxed italic">{PROMPT}</p>
              </div>

              <div className="flex flex-col sm:flex-row items-center gap-4 mb-6">
                <div className="w-full sm:w-auto flex items-center gap-3">
                  <span
                    className="text-4xl font-mono font-bold text-[#D4AF37] w-24 text-center"
                    data-testid="exercise-timer"
                  >
                    {format(remaining)}
                  </span>
                  <Button
                    onClick={() => setRunning((r) => !r)}
                    disabled={remaining === 0}
                    data-testid="exercise-timer-toggle"
                    className="bg-[#D4AF37] text-[#0a0a0a] hover:bg-[#FFD700] font-bold"
                  >
                    {running ? <><Pause className="w-4 h-4 mr-1" />PAUSE</> : <><Play className="w-4 h-4 mr-1" />{remaining === DURATION_SECONDS ? 'START' : 'RESUME'}</>}
                  </Button>
                  <Button
                    onClick={reset}
                    data-testid="exercise-timer-reset"
                    variant="outline"
                    className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10"
                  >
                    <RotateCcw className="w-4 h-4" />
                  </Button>
                </div>
                <div className="w-full sm:flex-1 h-2 bg-[#0a0a0a] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-[#D4AF37] to-[#FFD700] transition-all duration-1000"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>

              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                data-testid="exercise-textarea"
                placeholder="Start writing. No one else will read this unless you choose to send it to your own email below."
                rows={10}
                className="w-full bg-[#0a0a0a] border border-[#D4AF37]/20 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/40 outline-none rounded-xl p-5 text-white placeholder:text-gray-600 leading-relaxed resize-y font-serif text-lg"
              />

              <div className="flex flex-col sm:flex-row gap-3 mt-4 items-stretch sm:items-center">
                <input
                  type="email"
                  value={saveEmail}
                  onChange={(e) => setSaveEmail(e.target.value)}
                  data-testid="exercise-email"
                  placeholder="Optional: email this to yourself later"
                  className="flex-1 bg-[#0a0a0a] border border-[#D4AF37]/20 focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/40 outline-none rounded-lg px-4 py-3 text-white placeholder:text-gray-600 text-sm"
                />
                <Button
                  onClick={finish}
                  disabled={!text.trim() || saving}
                  data-testid="exercise-finish"
                  className="bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0a0a0a] hover:from-[#FFD700] hover:to-[#D4AF37] font-bold px-6 py-6 text-base"
                >
                  {saving ? 'SAVING...' : 'I AM DONE'}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default InteractiveExercise;
